"use client";

import { Page, PageHeader, SURFACE } from "@commerce/components/page";
import { Time } from "@commerce/components/time";
import { formatMoney } from "@commerce/shopper/types";
import { orpc } from "@shared/lib/orpc";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useQuery, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@ui/components/input-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ui/components/table";
import { ToggleGroup, ToggleGroupItem } from "@ui/components/toggle-group";
import { cn } from "@ui/lib";
import {
	ChevronRightIcon,
	SearchIcon,
	SearchXIcon,
	UsersIcon,
	XIcon,
} from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
	type CustomerRow,
	type CustomersSearch,
	customerName,
	listInputFrom,
	MARKETING_META,
} from "./meta";

const route = getRouteApi("/_saas/app/_org/$organizationSlug/customers/");
const ALL = "all";

export function customersQuery(
	organizationId: string,
	search: CustomersSearch,
) {
	const input = listInputFrom(organizationId, search);
	return orpc.customers.list.infiniteOptions({
		input: (cursor: string | undefined) => ({
			...input,
			...(cursor ? { cursor } : {}),
		}),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page) => page.nextCursor ?? undefined,
	});
}

/** Customers (spec 5.5): who orders, from which branch, and whether they hear about offers. */
export function CustomersScreen({
	organizationId,
}: {
	organizationId: string;
}) {
	const { organizationSlug } = route.useParams();
	const search = route.useSearch();
	const navigate = route.useNavigate();
	const setSearch = (patch: Partial<CustomersSearch>) =>
		navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
	const [text, setText] = useSearchText(search.q);

	const filters = useMemo(
		() => ({
			q: search.q,
			marketing: search.marketing,
			store: search.store,
		}),
		[search.q, search.marketing, search.store],
	);
	const deferred = useDeferredValue(filters);
	const query = useSuspenseInfiniteQuery(
		customersQuery(organizationId, deferred),
	);
	const rows = query.data.pages.flatMap((page) => page.customers);
	const filtered = Boolean(search.q || search.marketing || search.store);

	return (
		<Page width="wide">
			<PageHeader title="Customers" />
			<div className="flex flex-col gap-3 lg:flex-row lg:items-center">
				<SearchField
					text={text}
					onTextChange={setText}
					searching={query.isFetching && !query.isFetchingNextPage}
				/>
				<MarketingFilter
					value={search.marketing}
					onChange={(marketing) => setSearch({ marketing })}
				/>
				<BranchFilter
					organizationId={organizationId}
					value={search.store}
					onChange={(store) => setSearch({ store })}
				/>
			</div>

			<div
				className={cn(
					"transition-opacity duration-(--duration-dropdown)",
					deferred !== filters && "opacity-60",
				)}
				aria-busy={deferred !== filters}
			>
				{rows.length === 0 ? (
					<NoCustomers
						filtered={filtered}
						onShowAll={() => {
							setText("");
							setSearch({
								q: undefined,
								marketing: undefined,
								store: undefined,
							});
						}}
					/>
				) : (
					<CustomerRows
						rows={rows}
						organizationSlug={organizationSlug}
					/>
				)}
			</div>
			{query.hasNextPage ? (
				<Button
					variant="outline"
					className="self-center"
					onClick={() => query.fetchNextPage()}
					disabled={query.isFetchingNextPage}
				>
					{query.isFetchingNextPage ? (
						<Spinner data-icon="inline-start" />
					) : null}
					Show more customers
				</Button>
			) : null}
		</Page>
	);
}

/** The search box's text: typing stays local; the URL (and the server query) follow it debounced. */
function useSearchText(q: string | undefined) {
	const navigate = route.useNavigate();
	const [text, setText] = useState(q ?? "");
	const [debounced] = useDebouncedValue(text.trim(), { wait: 250 });
	useEffect(() => {
		if ((q ?? "") !== debounced) {
			navigate({
				search: (prev) => ({ ...prev, q: debounced || undefined }),
				replace: true,
			});
		}
	}, [debounced, q, navigate]);
	return [text, setText] as const;
}

function SearchField({
	text,
	onTextChange,
	searching,
}: {
	text: string;
	onTextChange: (text: string) => void;
	searching: boolean;
}) {
	return (
		<InputGroup className="lg:w-72">
			<InputGroupAddon>
				{searching ? <Spinner /> : <SearchIcon aria-hidden />}
			</InputGroupAddon>
			<InputGroupInput
				value={text}
				onChange={(e) => onTextChange(e.target.value)}
				placeholder="Name or phone"
				aria-label="Search customers"
				dir="auto"
			/>
			{text ? (
				<InputGroupAddon align="inline-end">
					<InputGroupButton
						size="icon-xs"
						onClick={() => onTextChange("")}
						aria-label="Clear search"
					>
						<XIcon />
					</InputGroupButton>
				</InputGroupAddon>
			) : null}
		</InputGroup>
	);
}

function MarketingFilter({
	value,
	onChange,
}: {
	value: CustomersSearch["marketing"];
	onChange: (marketing: CustomersSearch["marketing"]) => void;
}) {
	return (
		<div className="-mx-4 flex min-w-0 items-center gap-2 overflow-x-auto px-4 no-scrollbar lg:mx-0 lg:px-0">
			<ToggleGroup
				value={[value ?? ALL]}
				onValueChange={(next) => {
					const [picked] = next;
					if (picked) {
						onChange(
							picked === ALL
								? undefined
								: (picked as CustomersSearch["marketing"]),
						);
					}
				}}
				variant="outline"
				size="sm"
				aria-label="Filter by marketing choice"
			>
				<ToggleGroupItem value={ALL} className="px-3">
					All
				</ToggleGroupItem>
				<ToggleGroupItem value="opted_in" className="px-3">
					Opted in
				</ToggleGroupItem>
				<ToggleGroupItem value="opted_out" className="px-3">
					Opted out
				</ToggleGroupItem>
			</ToggleGroup>
		</div>
	);
}

/** Shown once the organization has more than one branch, or while a branch is picked. */
function BranchFilter({
	organizationId,
	value,
	onChange,
}: {
	organizationId: string;
	value: string | undefined;
	onChange: (store: string | undefined) => void;
}) {
	const stores = useQuery(
		orpc.stores.list.queryOptions({ input: { organizationId } }),
	);
	const storeItems: Record<string, string> = {
		[ALL]: "All branches",
		...Object.fromEntries(
			(stores.data?.stores ?? []).map((s) => [s.id, s.name]),
		),
	};
	if (Object.keys(storeItems).length <= 2 && !value) {
		return null;
	}
	return (
		<Select
			items={storeItems}
			value={value ?? ALL}
			onValueChange={(next) =>
				onChange(!next || next === ALL ? undefined : String(next))
			}
		>
			<SelectTrigger
				size="sm"
				aria-label="Filter by branch"
				className="w-full lg:ml-auto lg:w-52"
			>
				<SelectValue className="truncate" />
			</SelectTrigger>
			<SelectContent>
				{Object.entries(storeItems).map(([id, label]) => (
					<SelectItem key={id} value={id}>
						{label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

function NoCustomers({
	filtered,
	onShowAll,
}: {
	filtered: boolean;
	onShowAll: () => void;
}) {
	if (filtered) {
		return (
			<Empty className="rounded-3xl border">
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<SearchXIcon />
					</EmptyMedia>
					<EmptyTitle>No customers match</EmptyTitle>
				</EmptyHeader>
				<EmptyContent>
					<Button variant="outline" onClick={onShowAll}>
						Show every customer
					</Button>
				</EmptyContent>
			</Empty>
		);
	}
	return (
		<Empty className="rounded-3xl border">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<UsersIcon />
				</EmptyMedia>
				<EmptyTitle>No customers yet</EmptyTitle>
				<EmptyDescription>
					Shoppers who write on WhatsApp or place an order are added
					here.
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	);
}

function spent(row: CustomerRow) {
	return row.spent && row.currency
		? formatMoney(row.spent, row.currency)
		: "-";
}

function CustomerRows({
	rows,
	organizationSlug,
}: {
	rows: CustomerRow[];
	organizationSlug: string;
}) {
	const navigate = route.useNavigate();
	const open = (id: string) =>
		navigate({
			to: "/app/$organizationSlug/customers/$customerId",
			params: { organizationSlug, customerId: id },
		});
	return (
		<>
			<div className={cn(SURFACE, "max-lg:hidden")}>
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead className="pl-5">Name</TableHead>
							<TableHead>Phone</TableHead>
							<TableHead>Branch</TableHead>
							<TableHead className="text-right">Orders</TableHead>
							<TableHead className="text-right">Spent</TableHead>
							<TableHead className="text-right">
								Last seen
							</TableHead>
							<TableHead className="pr-5">Marketing</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{rows.map((row) => (
							<TableRow
								key={row.id}
								className="cursor-pointer"
								onClick={() => open(row.id)}
							>
								<TableCell className="py-2.5 pl-5">
									<Link
										to="/app/$organizationSlug/customers/$customerId"
										params={{
											organizationSlug,
											customerId: row.id,
										}}
										onClick={(e) => e.stopPropagation()}
										className="flex items-center gap-2 rounded-xl font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring"
									>
										<span
											dir="auto"
											className="max-w-56 truncate text-left"
										>
											{customerName(row)}
										</span>
										{row.linked ? (
											<Badge variant="outline">
												Linked
											</Badge>
										) : null}
									</Link>
								</TableCell>
								<TableCell className="py-2.5 font-mono text-muted-foreground text-xs">
									{row.phone ?? "-"}
								</TableCell>
								<TableCell className="max-w-40 truncate py-2.5 text-muted-foreground">
									{row.lastStore?.name ?? "-"}
								</TableCell>
								<TableCell className="py-2.5 text-right tabular">
									{row.orders}
								</TableCell>
								<TableCell className="py-2.5 text-right tabular">
									{spent(row)}
								</TableCell>
								<TableCell className="py-2.5 text-right text-muted-foreground">
									<Time
										at={new Date(row.lastSeenAt)}
										as="relative"
										label="Last seen"
									/>
								</TableCell>
								<TableCell className="py-2.5 pr-5">
									<Badge
										variant={
											MARKETING_META[row.marketing]
												.variant
										}
									>
										{MARKETING_META[row.marketing].label}
									</Badge>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			<ul className={cn(SURFACE, "divide-y divide-border/70 lg:hidden")}>
				{rows.map((row) => (
					<li key={row.id}>
						<Link
							to="/app/$organizationSlug/customers/$customerId"
							params={{ organizationSlug, customerId: row.id }}
							className="flex w-full items-center gap-3 px-4 py-3 text-left outline-none hover:bg-muted/50 focus-visible:bg-muted/50"
						>
							<div className="flex min-w-0 flex-1 flex-col gap-1">
								<div className="flex items-center justify-between gap-2">
									<span
										dir="auto"
										className="truncate text-left font-medium text-sm"
									>
										{customerName(row)}
									</span>
									<span className="shrink-0 text-sm tabular">
										{spent(row)}
									</span>
								</div>
								<div className="flex items-center justify-between gap-2 text-muted-foreground text-xs">
									<span className="truncate">
										{row.orders} order
										{row.orders === 1 ? "" : "s"}
										{row.lastStore
											? ` · ${row.lastStore.name}`
											: ""}
									</span>
									<Time
										at={new Date(row.lastSeenAt)}
										as="relative"
										label="Last seen"
										className="shrink-0"
									/>
								</div>
							</div>
							<ChevronRightIcon
								className="size-4 shrink-0 text-muted-foreground"
								aria-hidden
							/>
						</Link>
					</li>
				))}
			</ul>
		</>
	);
}

export function CustomersSkeleton() {
	return (
		<Page width="wide">
			<Skeleton className="h-8 w-40" />
			<div className="flex gap-3">
				<Skeleton className="h-8 w-72" />
				<Skeleton className="h-8 w-56 max-sm:hidden" />
			</div>
			<div
				className={cn(
					SURFACE,
					"flex flex-col divide-y divide-border/70",
				)}
			>
				{Array.from({ length: 8 }).map((_, i) => (
					<div key={i} className="flex items-center gap-6 px-5 py-3">
						<Skeleton className="h-4 w-40" />
						<Skeleton className="h-4 w-28 max-lg:hidden" />
						<Skeleton className="ml-auto h-4 w-16" />
					</div>
				))}
			</div>
		</Page>
	);
}
